import { CheckIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { enterStep } from '@/modules/careers/careers';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';

const YOUR_AVATAR = '/sign-up/emily-carter.jpg';
const FINLAY_AVATAR = '/finlay-slack-app-icon.png';

const benefits = [
  'Find the right candidates',
  'Personal outreach and screening',
  'Interviews booked in your calendar',
];

/**
 * The purple panel beside the sign-up card on wide screens: the pitch, a
 * short Slack exchange showing Finlay at work, and what it does for you.
 */
export const SignUpPitch = () => (
  <section
    aria-labelledby="sign-up-pitch-heading"
    className="flex flex-1 flex-col items-center justify-center overflow-hidden rounded-[20px] bg-[radial-gradient(70%_60%_at_100%_100%,rgba(150,120,255,0.45),rgba(150,120,255,0)),linear-gradient(160deg,#3f2299_0%,#5536c6_50%,#7d5cf6_100%)] px-12 py-14 text-white xl:px-14"
  >
    {/* One centred column, so on large screens the pitch stays together
        instead of the heading hugging the panel's left edge. */}
    <div className="flex w-full max-w-[480px] flex-col gap-10 min-[1920px]:max-w-[560px] min-[1920px]:gap-12">
      <div {...enterStep(0)} className="flex flex-col gap-2.5">
        <h2
          id="sign-up-pitch-heading"
          className="font-sans text-[34px] leading-[1.1] font-semibold tracking-[-0.0225em] min-[1920px]:text-[48px] xl:text-[40px]"
        >
          Your next hire starts <br />
          with a conversation.
        </h2>
        <p className="text-base leading-[1.4] text-white/75 min-[1920px]:text-xl xl:text-lg">
          Meet Finlay, your AI recruiter in Slack.
        </p>
      </div>

      <div className="flex flex-col gap-9">
        <div {...enterStep(1)}>
          <SlackExchange />
        </div>

        <ul {...enterStep(2)} className="flex flex-col gap-3 pl-2.5">
          {benefits.map((benefit) => (
            <li
              key={benefit}
              className="flex items-center gap-3.5 text-base min-[1920px]:text-lg"
            >
              <span className="flex size-7.5 shrink-0 items-center justify-center rounded-full bg-white/25">
                <CheckIcon className="size-4" strokeWidth={3} aria-hidden />
              </span>
              {benefit}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/** Decorative: the heading above already says what it shows. */
const SlackExchange = () => (
  <div
    aria-hidden="true"
    className="text-foreground font-slack overflow-hidden rounded-xl bg-white shadow-[0_16px_40px_rgba(30,12,90,0.25)] select-none"
  >
    <div className="flex h-12 items-center border-b border-[#e3e4ea] bg-[#f9f9fb]">
      <span className="flex h-full w-14 items-center justify-center border-r border-[#e3e4ea]">
        <SlackLogo className="size-5" />
      </span>
      <span className="px-4 text-[15px] font-bold"># hiring</span>
    </div>
    <div className="flex flex-col gap-5 px-6 pt-5 pb-6">
      <Message
        avatar={
          <img
            src={YOUR_AVATAR}
            alt=""
            className="size-9 rounded-lg object-cover"
          />
        }
        sender="You"
        time="9:14 AM"
      >
        Find us a senior product designer.
      </Message>
      <Message
        avatar={
          <img
            src={FINLAY_AVATAR}
            alt=""
            className="size-9 rounded-lg object-cover"
          />
        }
        sender="Finlay"
        time="9:15 AM"
      >
        On it. I’ll find, contact and screen candidates, then book interviews.
        <span className="mt-3 flex w-fit items-center gap-2 rounded-full bg-[#e3f8ee] py-1 pr-3 pl-1.5 text-[13px] font-medium text-[#137a48]">
          <span className="flex size-[22px] items-center justify-center rounded-full bg-[#2f9e6a] text-white">
            <CheckIcon className="size-3.5" strokeWidth={3} />
          </span>
          Interview booked
        </span>
      </Message>
    </div>
  </div>
);

interface MessageProps {
  avatar: ReactNode;
  sender: string;
  time: string;
  children: ReactNode;
}

const Message = ({ avatar, sender, time, children }: MessageProps) => (
  <div className="flex gap-4">
    <div className="shrink-0">{avatar}</div>
    <div className="min-w-0 text-[15px] leading-[1.47]">
      <p className="flex items-baseline gap-2.5">
        <span className="font-bold">{sender}</span>
        <span className="text-xs text-[#8b8fa3]">{time}</span>
      </p>
      <div className="flex flex-col">{children}</div>
    </div>
  </div>
);
